import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
    Brain, Zap, Server, CheckCircle, XCircle, AlertCircle, RefreshCw, Info,
    Download, Trash2, Check, HardDrive, Loader2, Plus, Key, Shield, Database, Cloud, Cpu
} from 'lucide-react';
import { logger } from '../utils/logger';
import { tokenManager } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The /api/config/* endpoints require a bearer token: the tenant middleware
// denies any request it cannot resolve to a valid context. Plain fetch() skips
// the axios interceptor in utils/api.js that normally attaches the token, so a
// header-less call comes back 401 and the JSON body parses into an object with
// none of the expected fields - which silently renders as empty dropdowns and
// off toggles rather than an error.
const authFetch = (url, options = {}) => {
    const token = tokenManager.getAccessToken();

    return fetch(url, {
        ...options,
        headers: {
            ...(options.headers || {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
};

const LLMConfigTab = () => {
    const [config, setConfig] = useState({
        llm_provider: 'groq',
        groq_model: 'llama-3.1-8b-instant',
        groq_api_key: '',
        openai_model: 'gpt-4o-mini',
        openai_api_key: '',
        enable_llm_classification: true
    });

    const [availableModels, setAvailableModels] = useState({
        groq: [],
        openai: [
            { value: 'gpt-5.4', label: 'GPT-5.4 (Best Intelligence, Agentic & Coding)' },
            { value: 'gpt-5.4-pro', label: 'GPT-5.4 Pro (Smarter, More Precise)' },
            { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini (Strong Mini for Coding)' },
            { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano (Cheapest, High Volume)' },
            { value: 'gpt-5', label: 'GPT-5 (Reasoning, Coding & Agentic)' },
            { value: 'gpt-5-mini', label: 'GPT-5 Mini (Fast, Cost-efficient)' },
            { value: 'gpt-4.1', label: 'GPT-4.1' },
            { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
            { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
            { value: 'gpt-4o', label: 'GPT-4o' },
            { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
            { value: 'o3', label: 'o3 (Reasoning, Complex Tasks)' },
            { value: 'o3-mini', label: 'o3 Mini (Reasoning, Cost-effective)' },
            { value: 'o4-mini', label: 'o4 Mini (Latest Reasoning)' },
            { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (Legacy)' },
            { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Legacy)' }
        ]
    });

    const [apiKeySource, setApiKeySource] = useState(null);
    const [refreshingGroqModels, setRefreshingGroqModels] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [saveMessage, setSaveMessage] = useState(null);

    // Groq API keys management state
    const [groqKeys, setGroqKeys] = useState([]);
    const [groqKeysStats, setGroqKeysStats] = useState({});
    const [newGroqKey, setNewGroqKey] = useState('');
    const [addingKey, setAddingKey] = useState(false);
    const [deletingKeyIndex, setDeletingKeyIndex] = useState(null);
    const [testingKeyIndex, setTestingKeyIndex] = useState(null);
    const [showKeyManager, setShowKeyManager] = useState(false);
    const [keyMessage, setKeyMessage] = useState(null);

    // Embedding configuration state
    const [embeddingConfig, setEmbeddingConfig] = useState({
        provider: 'local',
        dimension: 768,
        model_name: '',
        local_model: '',
        available_providers: [],
        available_local_models: [],
        credentials: {}
    });
    const [embeddingLoading, setEmbeddingLoading] = useState(false);
    const [embeddingSaving, setEmbeddingSaving] = useState(false);
    const [embeddingTesting, setEmbeddingTesting] = useState(false);
    const [embeddingTestResult, setEmbeddingTestResult] = useState(null);
    const [embeddingMessage, setEmbeddingMessage] = useState(null);
    const [cohereApiKey, setCohereApiKey] = useState('');
    const [openaiApiKey, setOpenaiApiKey] = useState('');
    const [showEmbeddingConfig, setShowEmbeddingConfig] = useState(false);

    // Reranker configuration state
    const [rerankerConfig, setRerankerConfig] = useState({
        enabled: true,
        provider: 'local',
        local_model: 'BAAI/bge-reranker-base',
        cohere_model: 'rerank-english-v3.0',
        cohere_api_key: { set: false, source: null, display: '' },
        available_providers: [],
        local_models: [
            { id: 'BAAI/bge-reranker-base', name: 'BGE Reranker Base', description: 'Good balance of speed and accuracy' },
            { id: 'BAAI/bge-reranker-large', name: 'BGE Reranker Large', description: 'Higher accuracy, slower' },
            { id: 'cross-encoder/ms-marco-MiniLM-L-6-v2', name: 'MS-MARCO MiniLM', description: 'Fast, lightweight model' },
        ],
        cohere_models: [
            { id: 'rerank-english-v3.0', name: 'Rerank English v3.0', description: 'Latest English reranking model' },
            { id: 'rerank-multilingual-v3.0', name: 'Rerank Multilingual v3.0', description: 'Supports 100+ languages' },
            { id: 'rerank-english-v2.0', name: 'Rerank English v2.0', description: 'Previous generation model' },
        ]
    });
    const [rerankerLoading, setRerankerLoading] = useState(false);
    const [rerankerSaving, setRerankerSaving] = useState(false);
    const [rerankerTesting, setRerankerTesting] = useState(false);
    const [rerankerTestResult, setRerankerTestResult] = useState(null);
    const [rerankerMessage, setRerankerMessage] = useState(null);
    const [rerankerCohereKey, setRerankerCohereKey] = useState('');
    const [showRerankerConfig, setShowRerankerConfig] = useState(false);

    useEffect(() => {
        loadConfig();
        loadEmbeddingConfig();
        loadRerankerConfig();
    }, []);

    useEffect(() => {
        if (config.llm_provider === 'groq' && showKeyManager) {
            loadGroqKeys();
        }
    }, [config.llm_provider, showKeyManager]);

    // Embedding Configuration Functions
    const loadEmbeddingConfig = async () => {
        try {
            setEmbeddingLoading(true);
            const response = await authFetch(`${API}/config/embeddings`);
            const data = await response.json();
            setEmbeddingConfig(data);
        } catch (error) {
            logger.error('Error loading embedding config:', error);
        } finally {
            setEmbeddingLoading(false);
        }
    };

    const saveEmbeddingConfig = async () => {
        try {
            setEmbeddingSaving(true);
            setEmbeddingMessage(null);

            const updateData = {
                provider: embeddingConfig.provider
            };

            // Include local model if using local provider
            if (embeddingConfig.provider === 'local' && embeddingConfig.local_model) {
                updateData.local_model = embeddingConfig.local_model;
            }

            // Include API keys if provided
            if (cohereApiKey && !cohereApiKey.startsWith('***')) {
                updateData.cohere_api_key = cohereApiKey;
            }
            if (openaiApiKey && !openaiApiKey.startsWith('***')) {
                updateData.openai_api_key = openaiApiKey;
            }

            const response = await authFetch(`${API}/config/embeddings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });

            const data = await response.json();

            if (data.success) {
                setEmbeddingMessage({
                    type: 'success',
                    text: data.message
                });
                await loadEmbeddingConfig();
            } else {
                setEmbeddingMessage({
                    type: 'error',
                    text: data.detail || 'Failed to save embedding configuration'
                });
            }
        } catch (error) {
            logger.error('Error saving embedding config:', error);
            setEmbeddingMessage({ type: 'error', text: 'Error saving configuration' });
        } finally {
            setEmbeddingSaving(false);
            setTimeout(() => setEmbeddingMessage(null), 5000);
        }
    };

    const testEmbeddingProvider = async (provider) => {
        try {
            setEmbeddingTesting(true);
            setEmbeddingTestResult(null);

            const testData = { provider };

            // Include local model if testing local provider
            if (provider === 'local' && embeddingConfig.local_model) {
                testData.model = embeddingConfig.local_model;
            }

            // Include API key if testing Cohere or OpenAI
            if (provider === 'cohere' && cohereApiKey) {
                testData.api_key = cohereApiKey;
            }
            if (provider === 'openai' && openaiApiKey) {
                testData.api_key = openaiApiKey;
            }

            const response = await authFetch(`${API}/config/embeddings/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(testData)
            });

            const data = await response.json();
            setEmbeddingTestResult(data);

            setTimeout(() => setEmbeddingTestResult(null), 5000);
        } catch (error) {
            setEmbeddingTestResult({ success: false, message: 'Test failed' });
        } finally {
            setEmbeddingTesting(false);
        }
    };

    // Default reranker models (fallback if API fails)
    const DEFAULT_LOCAL_RERANKER_MODELS = [
        { id: 'BAAI/bge-reranker-base', name: 'BGE Reranker Base', description: 'Good balance of speed and accuracy' },
        { id: 'BAAI/bge-reranker-large', name: 'BGE Reranker Large', description: 'Higher accuracy, slower' },
        { id: 'cross-encoder/ms-marco-MiniLM-L-6-v2', name: 'MS-MARCO MiniLM', description: 'Fast, lightweight model' },
    ];

    const DEFAULT_COHERE_RERANKER_MODELS = [
        { id: 'rerank-english-v3.0', name: 'Rerank English v3.0', description: 'Latest English reranking model' },
        { id: 'rerank-multilingual-v3.0', name: 'Rerank Multilingual v3.0', description: 'Supports 100+ languages' },
        { id: 'rerank-english-v2.0', name: 'Rerank English v2.0', description: 'Previous generation model' },
    ];

    // Reranker Configuration Functions
    const loadRerankerConfig = async () => {
        try {
            setRerankerLoading(true);
            const response = await authFetch(`${API}/config/reranker`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            logger.log('Reranker config loaded:', data);
            setRerankerConfig({
                enabled: data.enabled !== false,  // Default to true
                provider: data.current_provider || 'local',
                local_model: data.local_model || 'BAAI/bge-reranker-base',
                cohere_model: data.cohere_model || 'rerank-english-v3.0',
                cohere_api_key: data.cohere_api_key || { set: false, source: null, display: '' },
                available_providers: data.available_providers || [],
                local_models: data.local_models?.length > 0 ? data.local_models : DEFAULT_LOCAL_RERANKER_MODELS,
                cohere_models: data.cohere_models?.length > 0 ? data.cohere_models : DEFAULT_COHERE_RERANKER_MODELS,
                provider_source: data.provider_source
            });
        } catch (error) {
            logger.error('Error loading reranker config:', error);
            // Set defaults on error
            setRerankerConfig(prev => ({
                ...prev,
                local_models: DEFAULT_LOCAL_RERANKER_MODELS,
                cohere_models: DEFAULT_COHERE_RERANKER_MODELS
            }));
        } finally {
            setRerankerLoading(false);
        }
    };

    const saveRerankerConfig = async () => {
        try {
            setRerankerSaving(true);
            setRerankerMessage(null);

            const updateData = {
                enabled: rerankerConfig.enabled,
                provider: rerankerConfig.provider,
                local_model: rerankerConfig.local_model,
                cohere_model: rerankerConfig.cohere_model
            };

            // Include Cohere API key if provided and not masked
            if (rerankerCohereKey && !rerankerCohereKey.startsWith('***')) {
                updateData.cohere_api_key = rerankerCohereKey;
            }

            const response = await authFetch(`${API}/config/reranker`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });

            const data = await response.json();

            if (data.success) {
                setRerankerMessage({
                    type: 'success',
                    text: data.message
                });
                await loadRerankerConfig();
            } else {
                setRerankerMessage({
                    type: 'error',
                    text: data.detail || 'Failed to save reranker configuration'
                });
            }
        } catch (error) {
            logger.error('Error saving reranker config:', error);
            setRerankerMessage({ type: 'error', text: 'Error saving configuration' });
        } finally {
            setRerankerSaving(false);
            setTimeout(() => setRerankerMessage(null), 5000);
        }
    };

    const testRerankerProvider = async (provider) => {
        try {
            setRerankerTesting(true);
            setRerankerTestResult(null);

            const testData = { provider };

            // Include model for testing
            if (provider === 'local') {
                testData.model = rerankerConfig.local_model;
            } else if (provider === 'cohere') {
                testData.model = rerankerConfig.cohere_model;
                if (rerankerCohereKey && !rerankerCohereKey.startsWith('***')) {
                    testData.api_key = rerankerCohereKey;
                }
            }

            const response = await authFetch(`${API}/config/reranker/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(testData)
            });

            const data = await response.json();
            setRerankerTestResult(data);

            setTimeout(() => setRerankerTestResult(null), 5000);
        } catch (error) {
            setRerankerTestResult({ success: false, message: 'Test failed' });
        } finally {
            setRerankerTesting(false);
        }
    };

    const loadConfig = async () => {
        try {
            setLoading(true);
            const response = await authFetch(`${API}/config/llm`);
            const data = await response.json();

            setConfig({
                llm_provider: data.llm_provider,
                groq_model: data.groq_model,
                groq_api_key: data.groq_api_key || '',
                openai_model: data.openai_model || 'gpt-4o-mini',
                openai_api_key: data.openai_api_key || '',
                enable_llm_classification: data.enable_llm_classification
            });

            setApiKeySource(data.groq_api_key_source);

            setAvailableModels(prev => ({
                ...prev,
                groq: data.available_groq_models || []
            }));
        } catch (error) {
            logger.error('Error loading LLM config:', error);
        } finally {
            setLoading(false);
        }
    };

    const refreshGroqModels = async () => {
        try {
            setRefreshingGroqModels(true);
            const response = await authFetch(`${API}/config/llm`);
            const data = await response.json();
            setAvailableModels(prev => ({
                ...prev,
                groq: data.available_groq_models || []
            }));
        } catch (error) {
            logger.error('Error refreshing Groq models:', error);
        } finally {
            setRefreshingGroqModels(false);
        }
    };

    // Groq API Keys Management Functions
    const loadGroqKeys = async () => {
        try {
            const response = await authFetch(`${API}/config/groq-keys`);
            const data = await response.json();
            setGroqKeys(data.keys || []);
            setGroqKeysStats(data.stats || {});
        } catch (error) {
            logger.error('Error loading Groq keys:', error);
        }
    };

    const addGroqKey = async () => {
        if (!newGroqKey.trim()) return;

        try {
            setAddingKey(true);
            setKeyMessage(null);

            const response = await authFetch(`${API}/config/groq-keys`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ api_key: newGroqKey.trim() })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setKeyMessage({ type: 'success', text: 'API key added successfully!' });
                setNewGroqKey('');
                await loadGroqKeys();
            } else {
                setKeyMessage({ type: 'error', text: data.detail || 'Failed to add API key' });
            }
        } catch (error) {
            logger.error('Error adding Groq key:', error);
            setKeyMessage({ type: 'error', text: 'Error adding API key' });
        } finally {
            setAddingKey(false);
            setTimeout(() => setKeyMessage(null), 5000);
        }
    };

    const deleteGroqKey = async (keyIndex) => {
        if (!window.confirm('Are you sure you want to remove this API key?')) return;

        try {
            setDeletingKeyIndex(keyIndex);

            const response = await authFetch(`${API}/config/groq-keys/${keyIndex}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setKeyMessage({ type: 'success', text: 'API key removed successfully!' });
                await loadGroqKeys();
            } else {
                setKeyMessage({ type: 'error', text: data.detail || 'Failed to remove API key' });
            }
        } catch (error) {
            logger.error('Error deleting Groq key:', error);
            setKeyMessage({ type: 'error', text: 'Error removing API key' });
        } finally {
            setDeletingKeyIndex(null);
            setTimeout(() => setKeyMessage(null), 5000);
        }
    };

    const testGroqKey = async (keyIndex) => {
        try {
            setTestingKeyIndex(keyIndex);

            const response = await authFetch(`${API}/config/groq-keys/test/${keyIndex}`, {
                method: 'POST'
            });

            const data = await response.json();

            if (data.success) {
                setKeyMessage({
                    type: data.status === 'active' ? 'success' : 'warning',
                    text: data.message
                });
            } else {
                setKeyMessage({ type: 'error', text: data.message });
            }
        } catch (error) {
            logger.error('Error testing Groq key:', error);
            setKeyMessage({ type: 'error', text: 'Error testing API key' });
        } finally {
            setTestingKeyIndex(null);
            setTimeout(() => setKeyMessage(null), 5000);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setSaveMessage(null);

            const response = await authFetch(`${API}/config/llm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config)
            });

            const data = await response.json();

            if (data.success) {
                setSaveMessage({ type: 'success', text: 'LLM configuration saved successfully!' });
                setTimeout(() => setSaveMessage(null), 3000);
            } else {
                setSaveMessage({ type: 'error', text: 'Failed to save configuration' });
            }
        } catch (error) {
            setSaveMessage({ type: 'error', text: 'Error saving configuration' });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        try {
            setTesting(true);
            setTestResult(null);

            const testConfig = {
                provider: config.llm_provider,
                groq_api_key: config.groq_api_key,
                groq_model: config.groq_model,
                openai_api_key: config.openai_api_key,
                openai_model: config.openai_model
            };

            const response = await authFetch(`${API}/config/llm/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(testConfig)
            });

            const data = await response.json();
            setTestResult(data);

            setTimeout(() => setTestResult(null), 5000);
        } catch (error) {
            setTestResult({ success: false, message: 'Connection test failed' });
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* LLM Configuration - Single Card */}
            <Card className="border-2 border-blue-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                    <CardTitle className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-blue-600" />
                        LLM Configuration
                    </CardTitle>
                    <CardDescription>
                        Configure AI model providers for enhanced intent detection
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                    {/* Enable LLM Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                            <h3 className="font-semibold text-gray-900">Enable LLM Intent Classification</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Use AI models to improve intent detection for ambiguous queries
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={config.enable_llm_classification}
                                onChange={(e) => setConfig({ ...config, enable_llm_classification: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    </div>

                    {/* LLM Provider Selection */}
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Select Provider</h4>
                        <div className="grid grid-cols-2 gap-4">
                        <button
                            onClick={() => setConfig({ ...config, llm_provider: 'groq' })}
                            className={`p-4 border-2 rounded-lg transition-all ${config.llm_provider === 'groq'
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Zap className="w-5 h-5 text-blue-600" />
                                <span className="font-semibold">Groq</span>
                                <Badge variant="outline" className="ml-auto">Cloud</Badge>
                            </div>
                            <p className="text-sm text-gray-600">Fast, cloud-based inference</p>
                        </button>

                        <button
                            onClick={() => setConfig({ ...config, llm_provider: 'openai' })}
                            className={`p-4 border-2 rounded-lg transition-all ${config.llm_provider === 'openai'
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Cloud className="w-5 h-5 text-purple-600" />
                                <span className="font-semibold">OpenAI</span>
                                <Badge variant="outline" className="ml-auto">Cloud</Badge>
                            </div>
                            <p className="text-sm text-gray-600">GPT-4o, GPT-4, GPT-3.5</p>
                        </button>
                        </div>
                    </div>

                    {/* Groq Configuration */}
                    {config.llm_provider === 'groq' && (
                        <div className="border-t pt-6">
                            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                <Zap className="w-4 h-4 text-blue-600" />
                                Groq Configuration
                            </h4>
                            <div className="space-y-4">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium">API Key</label>
                                <button
                                    onClick={() => setShowKeyManager(!showKeyManager)}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                                >
                                    <Key className="w-3 h-3" />
                                    {showKeyManager ? 'Hide Keys' : 'Manage Multiple Keys'}
                                </button>
                            </div>
                            <Input
                                type="password"
                                value={config.groq_api_key}
                                onChange={(e) => setConfig({ ...config, groq_api_key: e.target.value })}
                                placeholder="Enter Groq API key..."
                                className="font-mono"
                            />
                            <div className="flex items-center justify-between mt-1">
                                <p className="text-xs text-gray-500">
                                    Get your API key from{' '}
                                    <a href="https://console.groq.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        console.groq.com
                                    </a>
                                </p>
                            </div>
                        </div>

                        {/* Multiple API Keys Manager */}
                        {showKeyManager && (
                            <div className="border-t pt-4 mt-4">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-blue-600" />
                                    API Key Rotation Pool
                                    <Badge variant="outline" className="ml-2">Beta</Badge>
                                </h4>
                                <p className="text-sm text-gray-500 mb-4">
                                    Add multiple API keys to distribute usage and avoid rate limits.
                                    Keys are rotated automatically using round-robin.
                                </p>

                                {/* Message display */}
                                {keyMessage && (
                                    <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                                        keyMessage.type === 'success'
                                            ? 'bg-green-50 border border-green-200 text-green-800'
                                            : keyMessage.type === 'warning'
                                            ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                                            : 'bg-red-50 border border-red-200 text-red-800'
                                    }`}>
                                        {keyMessage.type === 'success' ? (
                                            <CheckCircle className="w-4 h-4" />
                                        ) : keyMessage.type === 'warning' ? (
                                            <AlertCircle className="w-4 h-4" />
                                        ) : (
                                            <XCircle className="w-4 h-4" />
                                        )}
                                        <span className="text-sm">{keyMessage.text}</span>
                                    </div>
                                )}

                                {/* Add new key */}
                                <div className="flex gap-2 mb-4">
                                    <Input
                                        type="password"
                                        value={newGroqKey}
                                        onChange={(e) => setNewGroqKey(e.target.value)}
                                        placeholder="Enter new Groq API key (gsk_...)..."
                                        className="font-mono flex-1"
                                    />
                                    <Button
                                        onClick={addGroqKey}
                                        disabled={addingKey || !newGroqKey.trim()}
                                        className="bg-blue-600 hover:bg-blue-700"
                                    >
                                        {addingKey ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Plus className="w-4 h-4" />
                                        )}
                                        <span className="ml-1">Add Key</span>
                                    </Button>
                                </div>

                                {/* Current keys list */}
                                <div className="space-y-2">
                                    <h5 className="text-sm font-medium text-gray-700">
                                        Current Keys ({groqKeys.length})
                                    </h5>
                                    {groqKeys.length > 0 ? (
                                        groqKeys.map((keyInfo) => (
                                            <div
                                                key={keyInfo.id}
                                                className={`flex items-center justify-between p-3 rounded-lg border ${
                                                    keyInfo.status === 'rate_limited'
                                                        ? 'bg-yellow-50 border-yellow-300'
                                                        : 'bg-gray-50 border-gray-200'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Key className={`w-4 h-4 ${
                                                        keyInfo.status === 'rate_limited' ? 'text-yellow-600' : 'text-gray-400'
                                                    }`} />
                                                    <div>
                                                        <code className="text-sm font-mono">{keyInfo.masked_key}</code>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Badge
                                                                variant={keyInfo.status === 'active' ? 'default' : 'secondary'}
                                                                className={`text-xs ${
                                                                    keyInfo.status === 'active'
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-yellow-100 text-yellow-800'
                                                                }`}
                                                            >
                                                                {keyInfo.status === 'active' ? 'Active' : 'Rate Limited'}
                                                            </Badge>
                                                            <span className="text-xs text-gray-500">
                                                                {keyInfo.usage_count} requests
                                                            </span>
                                                            {keyInfo.source === 'environment' && (
                                                                <Badge variant="outline" className="text-xs">
                                                                    .env
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => testGroqKey(keyInfo.id)}
                                                        disabled={testingKeyIndex === keyInfo.id}
                                                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                                    >
                                                        {testingKeyIndex === keyInfo.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Zap className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                    {keyInfo.source !== 'environment' && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => deleteGroqKey(keyInfo.id)}
                                                            disabled={deletingKeyIndex === keyInfo.id}
                                                            className="text-red-600 border-red-300 hover:bg-red-50"
                                                        >
                                                            {deletingKeyIndex === keyInfo.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Trash2 className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded-lg">
                                            No API keys configured. Add keys above to enable rotation.
                                        </p>
                                    )}
                                </div>

                                {/* Stats */}
                                {groqKeys.length > 0 && (
                                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="text-sm text-blue-800">
                                            <strong>Rotation Status:</strong> {groqKeys.length} key(s) in pool •
                                            {groqKeysStats.failed_count > 0
                                                ? ` ${groqKeysStats.failed_count} temporarily rate-limited`
                                                : ' All keys active'
                                            } •
                                            Total requests: {groqKeysStats.total_usage || 0}
                                        </p>
                                    </div>
                                )}

                                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-xs text-yellow-800">
                                        <strong>Fallback:</strong> When all Groq keys are rate-limited, the system returns a high-demand message to users.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium">Model</label>
                                <button
                                    onClick={refreshGroqModels}
                                    disabled={refreshingGroqModels}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-3 h-3 ${refreshingGroqModels ? 'animate-spin' : ''}`} />
                                    {refreshingGroqModels ? 'Refreshing...' : 'Refresh models'}
                                </button>
                            </div>
                            <select
                                value={config.groq_model}
                                onChange={(e) => setConfig({ ...config, groq_model: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {availableModels.groq.map((model) => (
                                    <option key={model.value} value={model.value}>
                                        {model.label}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                                Models are fetched dynamically from Groq API
                            </p>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* OpenAI Configuration */}
                    {config.llm_provider === 'openai' && (
                        <div className="border-t pt-6">
                            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                <Cloud className="w-4 h-4 text-purple-600" />
                                OpenAI Configuration
                            </h4>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">API Key</label>
                                    <Input
                                        type="password"
                                        value={config.openai_api_key}
                                        onChange={(e) => setConfig({ ...config, openai_api_key: e.target.value })}
                                        placeholder="Enter OpenAI API key (sk-...)..."
                                        className="font-mono"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Get your API key from{' '}
                                        <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                            platform.openai.com
                                        </a>
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Model</label>
                                    <select
                                        value={config.openai_model}
                                        onChange={(e) => setConfig({ ...config, openai_model: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    >
                                        {availableModels.openai.map((model) => (
                                            <option key={model.value} value={model.value}>
                                                {model.label}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        GPT-4o is recommended for best quality. GPT-4o-mini is more cost-effective.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Save and Test Buttons */}
                    <div className="border-t pt-6 flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleTest}
                                disabled={testing || !config.enable_llm_classification}
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                {testing ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Testing...
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4" />
                                        Test Connection
                                    </>
                                )}
                            </Button>

                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                            >
                                {saving ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save LLM Configuration'
                                )}
                            </Button>
                        </div>

                        {testResult && (
                            <div className={`p-3 rounded-lg flex items-center gap-2 ${testResult.success
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : 'bg-red-50 border border-red-200 text-red-800'
                                }`}>
                                {testResult.success ? (
                                    <CheckCircle className="w-5 h-5" />
                                ) : (
                                    <XCircle className="w-5 h-5" />
                                )}
                                <span className="text-sm font-medium">{testResult.message}</span>
                            </div>
                        )}

                        {saveMessage && (
                            <div className={`p-3 rounded-lg flex items-center gap-2 ${saveMessage.type === 'success'
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : 'bg-red-50 border border-red-200 text-red-800'
                                }`}>
                                {saveMessage.type === 'success' ? (
                                    <CheckCircle className="w-5 h-5" />
                                ) : (
                                    <AlertCircle className="w-5 h-5" />
                                )}
                                <span className="text-sm font-medium">{saveMessage.text}</span>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Embedding Configuration Section */}
            <Card className="border-2 shadow-lg border-purple-200">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <Database className="w-5 h-5 text-purple-600" />
                            Embedding Configuration
                        </CardTitle>
                        <button
                            onClick={() => setShowEmbeddingConfig(!showEmbeddingConfig)}
                            className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                        >
                            {showEmbeddingConfig ? 'Hide' : 'Configure'}
                            <RefreshCw className={`w-3 h-3 ${showEmbeddingConfig ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                    <CardDescription>
                        Configure embedding provider for RAG retrieval quality
                    </CardDescription>
                </CardHeader>

                {showEmbeddingConfig && (
                    <CardContent className="space-y-4 pt-4">
                        {/* Current Status */}
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-purple-900">Current Provider</p>
                                    <p className="text-xs text-purple-700 mt-1">
                                        <strong>{embeddingConfig.provider?.toUpperCase()}</strong> •
                                        Model: {embeddingConfig.model_name || 'Not initialized'} •
                                        Dimension: {embeddingConfig.dimension}
                                    </p>
                                </div>
                                <Badge variant="outline" className="text-purple-700 border-purple-300">
                                    {embeddingConfig.provider_source || 'environment'}
                                </Badge>
                            </div>
                        </div>

                        {/* Provider Selection */}
                        <div>
                            <label className="block text-sm font-medium mb-2">Embedding Provider</label>
                            <div className="grid grid-cols-2 gap-3">
                                {/* Local Provider */}
                                <button
                                    onClick={() => setEmbeddingConfig({ ...embeddingConfig, provider: 'local' })}
                                    className={`p-3 border-2 rounded-lg transition-all text-left ${
                                        embeddingConfig.provider === 'local'
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Cpu className="w-4 h-4 text-green-600" />
                                        <span className="font-semibold text-sm">Local</span>
                                        <Badge variant="outline" className="ml-auto text-xs">Free</Badge>
                                    </div>
                                    <p className="text-xs text-gray-500">Sentence Transformers (768 dim)</p>
                                </button>

                                {/* AWS Titan Provider */}
                                <button
                                    onClick={() => setEmbeddingConfig({ ...embeddingConfig, provider: 'aws_titan' })}
                                    className={`p-3 border-2 rounded-lg transition-all text-left ${
                                        embeddingConfig.provider === 'aws_titan'
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Cloud className="w-4 h-4 text-orange-500" />
                                        <span className="font-semibold text-sm">AWS Titan</span>
                                        {embeddingConfig.credentials?.aws_titan?.configured && (
                                            <CheckCircle className="w-3 h-3 text-green-600 ml-auto" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">Amazon Bedrock (1024 dim)</p>
                                </button>

                                {/* Cohere Provider */}
                                <button
                                    onClick={() => setEmbeddingConfig({ ...embeddingConfig, provider: 'cohere' })}
                                    className={`p-3 border-2 rounded-lg transition-all text-left ${
                                        embeddingConfig.provider === 'cohere'
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Zap className="w-4 h-4 text-blue-600" />
                                        <span className="font-semibold text-sm">Cohere</span>
                                        {embeddingConfig.credentials?.cohere?.configured && (
                                            <CheckCircle className="w-3 h-3 text-green-600 ml-auto" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">embed-english-v3.0 (1024 dim)</p>
                                </button>

                                {/* OpenAI Provider */}
                                <button
                                    onClick={() => setEmbeddingConfig({ ...embeddingConfig, provider: 'openai' })}
                                    className={`p-3 border-2 rounded-lg transition-all text-left ${
                                        embeddingConfig.provider === 'openai'
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Brain className="w-4 h-4 text-emerald-600" />
                                        <span className="font-semibold text-sm">OpenAI</span>
                                        {embeddingConfig.credentials?.openai?.configured && (
                                            <CheckCircle className="w-3 h-3 text-green-600 ml-auto" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">text-embedding-3-large (1024 dim)</p>
                                </button>
                            </div>
                        </div>

                        {/* Local Model Selection */}
                        {embeddingConfig.provider === 'local' && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium">Local Embedding Model</label>
                                <select
                                    value={embeddingConfig.local_model || ''}
                                    onChange={(e) => setEmbeddingConfig({ ...embeddingConfig, local_model: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                    {embeddingConfig.available_local_models?.map((model) => (
                                        <option key={model.value} value={model.value}>
                                            {model.label} ({model.dimension} dim) - {model.description}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500">
                                    Recommended: <strong>Nomic Embed v1.5</strong> for best retrieval quality, or <strong>BGE Large</strong> for proven performance.
                                </p>
                            </div>
                        )}

                        {/* API Key Configuration for selected provider */}
                        {embeddingConfig.provider === 'aws_titan' && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                                <p className="text-sm text-orange-800">
                                    <strong>AWS Titan:</strong> Configure AWS credentials via environment variables or AWS CLI.
                                </p>
                                <p className="text-xs text-orange-700 mt-1">
                                    Required: <code className="bg-orange-100 px-1 rounded">AWS_ACCESS_KEY_ID</code>,
                                    <code className="bg-orange-100 px-1 rounded ml-1">AWS_SECRET_ACCESS_KEY</code>,
                                    <code className="bg-orange-100 px-1 rounded ml-1">AWS_REGION</code>
                                </p>
                                {embeddingConfig.credentials?.aws_titan?.configured && (
                                    <p className="text-xs text-green-700 mt-2">
                                        <CheckCircle className="w-3 h-3 inline mr-1" />
                                        AWS credentials configured (Region: {embeddingConfig.credentials.aws_titan.region})
                                    </p>
                                )}
                            </div>
                        )}

                        {embeddingConfig.provider === 'cohere' && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium">Cohere API Key</label>
                                <Input
                                    type="password"
                                    value={cohereApiKey || embeddingConfig.credentials?.cohere?.masked_key || ''}
                                    onChange={(e) => setCohereApiKey(e.target.value)}
                                    placeholder="Enter Cohere API key..."
                                    className="font-mono"
                                />
                                <p className="text-xs text-gray-500">
                                    Get your API key from{' '}
                                    <a href="https://dashboard.cohere.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        dashboard.cohere.com
                                    </a>
                                </p>
                            </div>
                        )}

                        {embeddingConfig.provider === 'openai' && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium">OpenAI API Key</label>
                                <Input
                                    type="password"
                                    value={openaiApiKey || embeddingConfig.credentials?.openai?.masked_key || ''}
                                    onChange={(e) => setOpenaiApiKey(e.target.value)}
                                    placeholder="Enter OpenAI API key..."
                                    className="font-mono"
                                />
                                <p className="text-xs text-gray-500">
                                    Get your API key from{' '}
                                    <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        platform.openai.com
                                    </a>
                                </p>
                            </div>
                        )}

                        {/* Test and Save Buttons */}
                        <div className="flex items-center gap-3 pt-2">
                            <Button
                                onClick={() => testEmbeddingProvider(embeddingConfig.provider)}
                                disabled={embeddingTesting}
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                {embeddingTesting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Testing...
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4" />
                                        Test Provider
                                    </>
                                )}
                            </Button>

                            <Button
                                onClick={saveEmbeddingConfig}
                                disabled={embeddingSaving}
                                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                            >
                                {embeddingSaving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Embedding Config'
                                )}
                            </Button>
                        </div>

                        {/* Test Result */}
                        {embeddingTestResult && (
                            <div className={`p-3 rounded-lg flex items-center gap-2 ${
                                embeddingTestResult.success
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : 'bg-red-50 border border-red-200 text-red-800'
                            }`}>
                                {embeddingTestResult.success ? (
                                    <CheckCircle className="w-5 h-5" />
                                ) : (
                                    <XCircle className="w-5 h-5" />
                                )}
                                <span className="text-sm font-medium">{embeddingTestResult.message}</span>
                            </div>
                        )}

                        {/* Save Message */}
                        {embeddingMessage && (
                            <div className={`p-3 rounded-lg flex items-center gap-2 ${
                                embeddingMessage.type === 'success'
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : 'bg-red-50 border border-red-200 text-red-800'
                            }`}>
                                {embeddingMessage.type === 'success' ? (
                                    <CheckCircle className="w-5 h-5" />
                                ) : (
                                    <AlertCircle className="w-5 h-5" />
                                )}
                                <span className="text-sm font-medium">{embeddingMessage.text}</span>
                            </div>
                        )}

                        {/* Re-index Warning */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-xs text-yellow-800">
                                <strong>Important:</strong> After changing embedding providers, you must re-index all content
                                from the Knowledge Hub for the changes to take effect. Different providers use different
                                embedding dimensions and cannot be mixed.
                            </p>
                        </div>
                    </CardContent>
                )}
            </Card>

            {/* Reranker Configuration Section */}
            <Card className="border-2 shadow-lg border-orange-200">
                <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="w-5 h-5 text-orange-600" />
                            Reranker Configuration
                        </CardTitle>
                        <button
                            onClick={() => setShowRerankerConfig(!showRerankerConfig)}
                            className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                        >
                            {showRerankerConfig ? 'Hide' : 'Configure'}
                            <RefreshCw className={`w-3 h-3 ${showRerankerConfig ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                    <CardDescription>
                        Configure reranker provider for improved search result relevance
                    </CardDescription>
                </CardHeader>

                {showRerankerConfig && (
                    <CardContent className="space-y-4 pt-4">
                        {/* Enable/Disable Toggle */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <div>
                                <p className="text-sm font-medium text-gray-900">Enable Reranking</p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Improves search relevance but adds latency. Disable for faster responses.
                                </p>
                            </div>
                            <button
                                onClick={() => setRerankerConfig({ ...rerankerConfig, enabled: !rerankerConfig.enabled })}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    rerankerConfig.enabled ? 'bg-orange-500' : 'bg-gray-300'
                                }`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        rerankerConfig.enabled ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                        </div>

                        {/* Current Status */}
                        {rerankerConfig.enabled && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-orange-900">Current Provider</p>
                                        <p className="text-xs text-orange-700 mt-1">
                                            <strong>{rerankerConfig.provider?.toUpperCase()}</strong> •
                                            Model: {rerankerConfig.provider === 'cohere' ? rerankerConfig.cohere_model : rerankerConfig.local_model}
                                        </p>
                                    </div>
                                    <Badge variant="outline" className="text-orange-700 border-orange-300">
                                        {rerankerConfig.provider_source || 'environment'}
                                    </Badge>
                                </div>
                            </div>
                        )}

                        {!rerankerConfig.enabled && (
                            <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-center">
                                <p className="text-sm text-gray-600">
                                    Reranking is disabled. Search results will be based on vector similarity only.
                                </p>
                            </div>
                        )}

                        {/* Provider Selection - only shown when enabled */}
                        {rerankerConfig.enabled && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Reranker Provider</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {/* Local Provider */}
                                        <button
                                            onClick={() => setRerankerConfig({ ...rerankerConfig, provider: 'local' })}
                                            className={`p-3 border-2 rounded-lg transition-all text-left ${
                                                rerankerConfig.provider === 'local'
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                <Cpu className="w-4 h-4 text-green-600" />
                                                <span className="font-semibold text-sm">Local Cross-Encoder</span>
                                                <Badge variant="outline" className="ml-auto text-xs">Free</Badge>
                                            </div>
                                            <p className="text-xs text-gray-500">BGE/MS-MARCO reranker models</p>
                                        </button>

                                        {/* Cohere Provider */}
                                        <button
                                            onClick={() => setRerankerConfig({ ...rerankerConfig, provider: 'cohere' })}
                                            className={`p-3 border-2 rounded-lg transition-all text-left ${
                                                rerankerConfig.provider === 'cohere'
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                <Zap className="w-4 h-4 text-blue-600" />
                                                <span className="font-semibold text-sm">Cohere Rerank</span>
                                                {rerankerConfig.cohere_api_key?.set && (
                                                    <CheckCircle className="w-3 h-3 text-green-600 ml-auto" />
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500">High-quality cloud reranking</p>
                                        </button>
                                    </div>
                                </div>

                                {/* Local Model Selection */}
                                {rerankerConfig.provider === 'local' && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium">Local Reranker Model</label>
                                <select
                                    value={rerankerConfig.local_model || 'BAAI/bge-reranker-base'}
                                    onChange={(e) => setRerankerConfig({ ...rerankerConfig, local_model: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                                >
                                    {rerankerConfig.local_models?.map((model) => (
                                        <option key={model.id} value={model.id}>
                                            {model.name} - {model.description}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500">
                                    Recommended: <strong>BGE Reranker Base</strong> for best balance of speed and accuracy.
                                </p>
                            </div>
                        )}

                        {/* Cohere Configuration */}
                        {rerankerConfig.provider === 'cohere' && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium">Cohere Rerank Model</label>
                                    <select
                                        value={rerankerConfig.cohere_model || 'rerank-english-v3.0'}
                                        onChange={(e) => setRerankerConfig({ ...rerankerConfig, cohere_model: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    >
                                        {rerankerConfig.cohere_models?.map((model) => (
                                            <option key={model.id} value={model.id}>
                                                {model.name} - {model.description}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium">Cohere API Key</label>
                                    <Input
                                        type="password"
                                        value={rerankerCohereKey || (rerankerConfig.cohere_api_key?.set ? '***************' : '')}
                                        onChange={(e) => setRerankerCohereKey(e.target.value)}
                                        placeholder="Enter Cohere API key..."
                                        className="font-mono"
                                    />
                                    <p className="text-xs text-gray-500">
                                        Uses same API key as Cohere embeddings. Get your key from{' '}
                                        <a href="https://dashboard.cohere.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                            dashboard.cohere.com
                                        </a>
                                    </p>
                                    {rerankerConfig.cohere_api_key?.set && (
                                        <p className="text-xs text-green-600 flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" />
                                            API key configured from {rerankerConfig.cohere_api_key.source}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                            </>
                        )}

                        {/* Test and Save Buttons */}
                        <div className="flex items-center gap-3 pt-2">
                            <Button
                                onClick={() => testRerankerProvider(rerankerConfig.provider)}
                                disabled={rerankerTesting || !rerankerConfig.enabled}
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                {rerankerTesting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Testing...
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4" />
                                        Test Provider
                                    </>
                                )}
                            </Button>

                            <Button
                                onClick={saveRerankerConfig}
                                disabled={rerankerSaving}
                                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
                            >
                                {rerankerSaving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Reranker Config'
                                )}
                            </Button>
                        </div>

                        {/* Test Result */}
                        {rerankerTestResult && (
                            <div className={`p-3 rounded-lg flex items-center gap-2 ${
                                rerankerTestResult.success
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : 'bg-red-50 border border-red-200 text-red-800'
                            }`}>
                                {rerankerTestResult.success ? (
                                    <CheckCircle className="w-5 h-5" />
                                ) : (
                                    <XCircle className="w-5 h-5" />
                                )}
                                <span className="text-sm font-medium">{rerankerTestResult.message}</span>
                            </div>
                        )}

                        {/* Save Message */}
                        {rerankerMessage && (
                            <div className={`p-3 rounded-lg flex items-center gap-2 ${
                                rerankerMessage.type === 'success'
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : 'bg-red-50 border border-red-200 text-red-800'
                            }`}>
                                {rerankerMessage.type === 'success' ? (
                                    <CheckCircle className="w-5 h-5" />
                                ) : (
                                    <AlertCircle className="w-5 h-5" />
                                )}
                                <span className="text-sm font-medium">{rerankerMessage.text}</span>
                            </div>
                        )}

                        {/* Info Note */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs text-blue-800">
                                <strong>Note:</strong> Reranking improves search result relevance by scoring query-document pairs.
                                Cohere provides higher quality reranking but requires an API key. Local models are free but may be slower.
                            </p>
                        </div>
                    </CardContent>
                )}
            </Card>
        </div>
    );
};

export default LLMConfigTab;